import React, { useEffect, useRef, useState, useCallback } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AppIcon from "../../../components/ui/AppIcon";
import { useAuth } from "../../../context/AuthContext";
import { useChat } from "../../../context/ChatContext";

// ── Markdown element styling (no typography plugin in this project) ──────────
const mdComponents = {
  h1: ({ node, ...p }) => <h1 className="text-xl font-bold text-text-primary mt-3 mb-2" {...p} />,
  h2: ({ node, ...p }) => <h2 className="text-lg font-bold text-text-primary mt-3 mb-1.5" {...p} />,
  h3: ({ node, ...p }) => <h3 className="text-base font-bold text-text-primary mt-2 mb-1" {...p} />,
  p: ({ node, ...p }) => <p className="text-[14px] leading-relaxed text-text-primary my-1.5" {...p} />,
  ul: ({ node, ...p }) => (
    <ul className="list-disc pl-5 my-1.5 space-y-1 text-[14px] text-text-primary" {...p} />
  ),
  ol: ({ node, ...p }) => (
    <ol className="list-decimal pl-5 my-1.5 space-y-1 text-[14px] text-text-primary" {...p} />
  ),
  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
  a: ({ node, ...p }) => (
    <a className="text-primary underline" target="_blank" rel="noreferrer" {...p} />
  ),
  pre: ({ node, ...p }) => (
    <pre className="p-3 rounded-xl bg-black/5 overflow-x-auto text-[13px] font-mono my-2" {...p} />
  ),
  code: ({ node, ...p }) => (
    <code className="px-1 py-0.5 rounded bg-black/5 text-[13px] font-mono" {...p} />
  ),
  blockquote: ({ node, ...p }) => (
    <blockquote className="border-l-2 border-border-strong pl-3 italic text-text-secondary my-2" {...p} />
  ),
  strong: ({ node, ...p }) => <strong className="font-bold" {...p} />,
  hr: () => <hr className="my-3 border-border-subtle" />,
  input: ({ node, ...p }) => (
    <input {...p} disabled className="mr-1.5 align-middle accent-primary" />
  ),
};

function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff)) return "";
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ts).toLocaleDateString();
}

function MarkdownPreview({ content }) {
  if (!content || !content.trim()) {
    return <p className="text-[13px] text-text-muted italic">Nothing to preview yet…</p>;
  }
  return (
    <div className="break-words">
      <Markdown remarkPlugins={[remarkGfm]} components={mdComponents}>
        {content}
      </Markdown>
    </div>
  );
}

// ── A single canvas block: read-only, or editable when you own it ────────────
function CanvasBlock({ block, isOwn, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(block.content || "");
  const saveTimer = useRef(null);

  // Keep the draft in sync with remote changes ONLY while not editing.
  useEffect(() => {
    if (!editing) setDraft(block.content || "");
  }, [block.content, editing]);

  const scheduleSave = useCallback(
    (value) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        onSave(block._id, value);
      }, 700);
    },
    [block._id, onSave],
  );

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const finishEditing = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setEditing(false);
    if ((draft || "") !== (block.content || "")) onSave(block._id, draft);
  };

  return (
    <div className="group relative rounded-2xl border border-transparent hover:border-border-subtle hover:bg-app-surface/60 transition-colors px-3 py-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[11px] font-bold text-text-secondary">
          {isOwn ? "You" : block.authorName || "Someone"}
        </span>
        <span className="text-[10px] text-text-muted">
          {timeAgo(block.updatedAt || block.createdAt)}
        </span>
        {isOwn && !editing && (
          <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 rounded-lg text-text-muted hover:text-primary hover:bg-black/5 transition-all"
              title="Edit"
            >
              <AppIcon name="PencilIcon" size={14} />
            </button>
            <button
              onClick={() => onDelete(block._id, block.content)}
              className="p-1.5 rounded-lg text-text-muted hover:text-red-500 hover:bg-black/5 transition-all"
              title="Delete"
            >
              <AppIcon name="TrashIcon" size={14} />
            </button>
          </span>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              scheduleSave(e.target.value);
            }}
            rows={Math.min(16, Math.max(3, draft.split("\n").length))}
            className="w-full bg-app-bg border border-border-subtle rounded-xl px-3 py-2 text-[14px] font-mono text-text-primary outline-none focus:border-primary/50 resize-none custom-scrollbar"
            placeholder="Write markdown…"
          />
          <div className="rounded-xl border border-border-subtle bg-app-bg/40 px-3 py-2">
            <MarkdownPreview content={draft} />
          </div>
          <div className="flex justify-end">
            <button
              onClick={finishEditing}
              className="px-3 py-1.5 rounded-lg bg-primary text-white text-[12px] font-bold hover:bg-primary-dark transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <MarkdownPreview content={block.content} />
      )}
    </div>
  );
}

/**
 * CanvasView — the per-conversation document. Ordered markdown blocks, each
 * owned by its author: you can edit/delete only your own; anyone can append.
 * Rendered full-width in the chat column when the Canvas tab is active.
 */
export default function CanvasView({ conversationId, chatType }) {
  const { user } = useAuth();
  const {
    canvasByConversation,
    fetchCanvas,
    addCanvasBlock,
    updateCanvasBlock,
    deleteCanvasBlock,
  } = useChat();

  const myUserId = (user?._id || user?.id)?.toString();
  const blocks = canvasByConversation[conversationId] || [];

  const [newContent, setNewContent] = useState("");
  const [showAddPreview, setShowAddPreview] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (conversationId) fetchCanvas(conversationId);
  }, [conversationId, fetchCanvas]);

  const handleAdd = async () => {
    const content = newContent.trim();
    if (!content || adding) return;
    setAdding(true);
    try {
      await addCanvasBlock(conversationId, content, chatType);
      setNewContent("");
      setShowAddPreview(false);
    } catch (e) {
      console.error("Canvas add failed:", e?.message);
    } finally {
      setAdding(false);
    }
  };

  const handleSave = useCallback(
    (blockId, content) => {
      updateCanvasBlock(conversationId, blockId, content).catch((e) =>
        console.error("Canvas update failed:", e?.message),
      );
    },
    [conversationId, updateCanvasBlock],
  );

  const handleDelete = useCallback(
    (blockId, content) => {
      if (content && content.trim()) {
        // eslint-disable-next-line no-restricted-globals, no-alert
        if (!window.confirm("Delete this canvas block?")) return;
      }
      deleteCanvasBlock(conversationId, blockId).catch((e) =>
        console.error("Canvas delete failed:", e?.message),
      );
    },
    [conversationId, deleteCanvasBlock],
  );

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar bg-app-bg px-3 md:px-6 py-4 md:py-6">
      <div className="max-w-3xl mx-auto">
        {/* Blocks */}
        {blocks.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-12 text-text-muted">
            <div className="w-16 h-16 rounded-2xl bg-app-surface border border-border-subtle flex items-center justify-center mb-4 shadow-subtle">
              <AppIcon name="DocumentTextIcon" size={30} className="text-primary/60" />
            </div>
            <p className="text-sm font-bold text-text-primary">No canvas yet</p>
            <p className="text-xs">Add the first note below — everyone here will see it.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {blocks.map((block) => (
              <CanvasBlock
                key={block._id}
                block={block}
                isOwn={(block.authorId || "")?.toString() === myUserId}
                onSave={handleSave}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Add composer */}
        <div className="mt-4 rounded-2xl border border-border-subtle bg-app-surface shadow-subtle p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
              Add to canvas
            </span>
            <button
              onClick={() => setShowAddPreview((s) => !s)}
              className={`text-[11px] font-bold px-2 py-1 rounded-lg transition-colors ${
                showAddPreview
                  ? "bg-primary/10 text-primary"
                  : "text-text-muted hover:text-text-primary hover:bg-black/5"
              }`}
            >
              {showAddPreview ? "Edit" : "Preview"}
            </button>
          </div>

          {showAddPreview ? (
            <div className="rounded-xl border border-border-subtle bg-app-bg/40 px-3 py-2 min-h-[64px]">
              <MarkdownPreview content={newContent} />
            </div>
          ) : (
            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              rows={Math.min(12, Math.max(3, newContent.split("\n").length))}
              placeholder="Write markdown — # headings, - bullets, - [ ] tasks, **bold**, links…"
              className="w-full bg-app-bg border border-border-subtle rounded-xl px-3 py-2 text-[14px] font-mono text-text-primary placeholder:text-text-muted outline-none focus:border-primary/50 resize-none custom-scrollbar"
            />
          )}

          <div className="flex justify-end mt-2">
            <button
              onClick={handleAdd}
              disabled={!newContent.trim() || adding}
              className={`px-4 py-2 rounded-xl text-[13px] font-bold flex items-center gap-2 transition-all shadow-premium ${
                newContent.trim() && !adding
                  ? "bg-primary text-white hover:bg-primary-dark"
                  : "bg-black/5 text-text-muted cursor-not-allowed"
              }`}
            >
              <AppIcon name="PlusIcon" size={14} />
              Add to canvas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
